import { type InputHTMLAttributes, type SelectHTMLAttributes, type TextareaHTMLAttributes, forwardRef, type ReactNode, useId } from 'react';
import { clsx } from 'clsx';

const labelClass = 'mb-1 block text-sm font-medium text-[var(--color-ink)]';
const inputClass = clsx(
  'w-full min-h-11 rounded-md border border-[var(--color-rule)] bg-[var(--color-surface)] px-3 py-2.5 text-base font-medium text-[var(--color-ink)] shadow-[var(--shadow-brutal-sm)] md:min-h-0 md:py-2 md:text-sm',
  'focus:border-[var(--color-rule)] focus:outline-none focus:ring-[3px] focus:ring-[var(--color-focus)] focus:ring-offset-1',
  'disabled:cursor-not-allowed disabled:bg-[var(--color-paper-2)] disabled:opacity-60',
  'placeholder:text-[var(--color-subtle)]',
);

interface FieldProps {
  label: string;
  error?: string;
  children: ReactNode;
}

export function Field({ label, error, children }: FieldProps) {
  return (
    <div>
      <label className={labelClass}>{label}</label>
      {children}
      {error && <p className="mt-1 text-sm font-semibold text-[var(--color-danger)]" role="alert">{error}</p>}
    </div>
  );
}

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, className, id, ...props }, ref) => {
    const autoId = useId();
    const inputId = id ?? autoId;
    return (
      <div>
        {label && <label htmlFor={inputId} className={labelClass}>{label}</label>}
        <input ref={ref} id={inputId} className={clsx(inputClass, className)} {...props} />
        {error && <p className="mt-1 text-sm font-semibold text-[var(--color-danger)]" role="alert">{error}</p>}
      </div>
    );
  },
);
Input.displayName = 'Input';

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, error, className, children, id, ...props }, ref) => {
    const autoId = useId();
    const selectId = id ?? autoId;
    return (
      <div>
        {label && <label htmlFor={selectId} className={labelClass}>{label}</label>}
        <select ref={ref} id={selectId} className={clsx(inputClass, className)} {...props}>
          {children}
        </select>
        {error && <p className="mt-1 text-sm font-semibold text-[var(--color-danger)]" role="alert">{error}</p>}
      </div>
    );
  },
);
Select.displayName = 'Select';

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, className, id, ...props }, ref) => {
    const autoId = useId();
    const textareaId = id ?? autoId;
    return (
      <div>
        {label && <label htmlFor={textareaId} className={labelClass}>{label}</label>}
        <textarea ref={ref} id={textareaId} className={clsx(inputClass, className)} {...props} />
        {error && <p className="mt-1 text-sm font-semibold text-[var(--color-danger)]" role="alert">{error}</p>}
      </div>
    );
  },
);
Textarea.displayName = 'Textarea';
